import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
import { useFeedback } from '../../hooks/useFeedback';
import type { FreeformCategory } from '../../lib/feedback';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORIES: { value: FreeformCategory; label: string }[] = [
  { value: 'bug', label: 'Bug Report' },
  { value: 'idea', label: 'Feature Idea' },
  { value: 'praise', label: 'Something I Love' },
  { value: 'other', label: 'Other' },
];

const MAX_LENGTH = 2000;

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [category, setCategory] = useState<FreeformCategory>('idea');
  const [message, setMessage] = useState('');
  const [includePage, setIncludePage] = useState(true);
  const [success, setSuccess] = useState(false);
  const { submit, isSubmitting, error } = useFeedback();
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Escape key dismiss
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCategory('idea');
      setMessage('');
      setIncludePage(true);
      setSuccess(false);
    }
  }, [isOpen]);

  // Cleanup success timer on unmount
  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const handleSubmit = async () => {
    if (!message.trim()) return;

    await submit({
      feedbackType: 'freeform',
      freeform: {
        category,
        message: message.trim(),
        ...(includePage ? { attachedRoute: window.location.pathname } : {}),
      },
    }).catch(() => {
      // error is already set in useFeedback; do not close
    });

    // Only proceed to success if no error thrown
    if (!error) {
      setSuccess(true);
      successTimerRef.current = setTimeout(() => {
        onClose();
      }, 1500);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ type: 'spring', damping: 30, stiffness: 400 }}
            className="w-full max-w-md bg-white dark:bg-[hsl(220,15%,12%)] rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 p-5 border-b border-gray-100 dark:border-white/5">
              <h2 className="text-meta font-semibold text-gray-900 dark:text-white">
                Share Feedback
              </h2>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              {success ? (
                <div className="py-6 text-center">
                  <p className="text-meta font-medium text-emerald-600 dark:text-emerald-400">
                    Thanks for your feedback!
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Category */}
                  <div className="space-y-4">
                    {CATEGORIES.map((cat) => (
                      <label
                        key={cat.value}
                        className="flex items-center gap-2.5 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="feedback-category"
                          value={cat.value}
                          checked={category === cat.value}
                          onChange={() => setCategory(cat.value)}
                          disabled={isSubmitting}
                          className="accent-primary h-4 w-4 flex-shrink-0"
                        />
                        <span className="text-meta text-gray-700 dark:text-gray-300">
                          {cat.label}
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Textarea */}
                  <div>
                    <textarea
                      value={message}
                      onChange={(e) =>
                        setMessage(e.target.value.slice(0, MAX_LENGTH))
                      }
                      placeholder="Tell us more..."
                      rows={5}
                      disabled={isSubmitting}
                      maxLength={MAX_LENGTH}
                      className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-meta text-gray-800 dark:text-gray-200 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 placeholder:text-gray-500 dark:placeholder:text-gray-400 resize-y disabled:opacity-60"
                    />
                    <div className="flex items-start justify-between mt-1 gap-2">
                      <p className="text-micro text-gray-400 dark:text-gray-500">
                        Don't include sensitive or personal information
                      </p>
                      <span className="text-micro text-gray-400 dark:text-gray-500 flex-shrink-0">
                        {message.length}/{MAX_LENGTH}
                      </span>
                    </div>
                  </div>

                  {/* Include page checkbox */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includePage}
                      onChange={(e) => setIncludePage(e.target.checked)}
                      disabled={isSubmitting}
                      className="accent-primary h-4 w-4 flex-shrink-0"
                    />
                    <span className="text-micro text-gray-500 dark:text-gray-400">
                      Include current page
                    </span>
                  </label>

                  {/* Error */}
                  {error && (
                    <p className="text-micro text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-2.5">
                      {error}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      onClick={onClose}
                      disabled={isSubmitting}
                      className="px-4 py-2 text-meta text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => void handleSubmit()}
                      disabled={isSubmitting || !message.trim()}
                      className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        'Submit'
                      )}
                    </motion.button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
